import {
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  Output,
  SimpleChanges,
  ChangeDetectorRef,
} from '@angular/core';
import { Subscription } from 'rxjs';
import { FilePreviewService } from '../../../../services/file-preview-service/file-preview.service';
import { FilePreviewData } from '../../../models/file-preview.models';
import type { AgentFilePreviewRequest } from '../../../../shared/components/agent-file-attachment-card/agent-file-attachment-card.component';
import { LoggerService } from '../../../../services/logger-services/logger.service';

@Component({
  selector: 'app-agent-document-preview-modal',
  templateUrl: './agent-document-preview-modal.component.html',
  styleUrls: ['./agent-document-preview-modal.component.scss'],
})
export class AgentDocumentPreviewModalComponent implements OnChanges, OnDestroy {
  @Input() isOpen = false;
  @Input() previewRequest: AgentFilePreviewRequest | null = null;

  @Output() closed = new EventEmitter<void>();
  @Output() downloadCompleted = new EventEmitter<string>();
  @Output() downloadFailed = new EventEmitter<string>();

  isLoading = false;
  errorMessage = '';
  previewData: FilePreviewData | null = null;
  zoomLevel = 100;
  pdfSrc: string | null = null;
  pdfZoom = 1.0;
  showPdfViewer = false;
  pdfLoaded = false;
  firstPageRendered = false;
  currentPage = 1;
  totalPages = 0;

  private readonly subscriptions = new Subscription();
  private readonly minZoom = 50;
  private readonly maxZoom = 200;
  private readonly zoomStep = 10;
  private activeBlobUrl: string | null = null;

  constructor(
    private readonly filePreviewService: FilePreviewService,
    private readonly logger: LoggerService,
    private readonly cdr: ChangeDetectorRef
  ) {}

  ngOnChanges(changes: SimpleChanges): void {
    if ((changes['isOpen'] || changes['previewRequest']) && this.isOpen && this.previewRequest) {
      this.loadPreview();
    }
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
    this.cleanupBlobUrl();
  }

  close(): void {
    this.cleanupBlobUrl();
    this.closed.emit();
  }

  onBackdropClick(event: MouseEvent): void {
    if ((event.target as HTMLElement).classList.contains('agent-doc-preview-overlay')) {
      this.close();
    }
  }

  zoomIn(): void {
    if (this.zoomLevel < this.maxZoom) {
      this.zoomLevel += this.zoomStep;
      this.updatePdfZoom();
    }
  }

  zoomOut(): void {
    if (this.zoomLevel > this.minZoom) {
      this.zoomLevel -= this.zoomStep;
      this.updatePdfZoom();
    }
  }

  get canOpenInNewTab(): boolean {
    return !this.isLoading && !!this.activeBlobUrl;
  }

  openInNewTab(): void {
    if (!this.activeBlobUrl) {
      return;
    }

    void fetch(this.activeBlobUrl)
      .then((response) => response.blob())
      .then((blob) => {
        const pdfBlob = blob.type === 'application/pdf'
          ? blob
          : new Blob([blob], { type: 'application/pdf' });
        const tabUrl = URL.createObjectURL(pdfBlob);
        window.open(tabUrl, '_blank', 'noopener,noreferrer');
      })
      .catch((error) => {
        this.logger.logError(
          (error as Error)?.message ?? 'Failed to open PDF in new tab',
          'AgentDocumentPreviewModalComponent.openInNewTab'
        );
      });
  }

  downloadFile(): void {
    const fileName = this.previewRequest?.fileName;
    if (!fileName) {
      return;
    }

    if (this.activeBlobUrl) {
      this.triggerDownload(this.activeBlobUrl, fileName);
      this.downloadCompleted.emit(fileName);
      return;
    }

    const presignedUrl = this.previewRequest?.presignedUrl;
    if (!presignedUrl) {
      this.downloadFailed.emit(fileName);
      return;
    }

    this.fetchAndDownload(presignedUrl, fileName)
      .then(() => this.downloadCompleted.emit(fileName))
      .catch((error) => {
        this.logger.logError(
          (error as Error)?.message ?? 'Modal download failed',
          'AgentDocumentPreviewModalComponent.downloadFile'
        );
        this.downloadFailed.emit(fileName);
      });
  }

  onPdfLoadComplete(pdf: { numPages: number }): void {
    this.pdfLoaded = true;
    this.totalPages = pdf?.numPages ?? 0;
    this.cdr.detectChanges();
  }

  onPageRendered(): void {
    this.firstPageRendered = true;
    this.cdr.detectChanges();
  }

  onPdfError(error: unknown): void {
    this.errorMessage = 'Failed to render PDF preview';
    this.logger.logError(
      (error as Error)?.message ?? 'PDF render error',
      'AgentDocumentPreviewModalComponent.onPdfError'
    );
    this.cdr.detectChanges();
  }

  private loadPreview(): void {
    if (!this.previewRequest?.presignedUrl) {
      this.errorMessage = 'No file to preview';
      return;
    }

    this.isLoading = true;
    this.errorMessage = '';
    this.previewData = null;
    this.pdfLoaded = false;
    this.firstPageRendered = false;
    this.showPdfViewer = false;
    this.cleanupBlobUrl();

    const sub = this.filePreviewService
      .getFilePreview({
        presignedUrl: this.previewRequest.presignedUrl,
        fileName: this.previewRequest.fileName,
        fileType: 'pdf',
      })
      .subscribe({
        next: (data) => this.handlePreviewData(data),
        error: (error) => {
          this.isLoading = false;
          this.errorMessage = error?.message ?? 'Failed to load file preview';
          this.cdr.detectChanges();
        },
      });
    this.subscriptions.add(sub);
  }

  private handlePreviewData(data: FilePreviewData): void {
    this.isLoading = false;
    if (data.error) {
      this.errorMessage = data.error;
      this.cdr.detectChanges();
      return;
    }

    this.previewData = data;
    if (data.type === 'pdf' && data.blobUrl) {
      this.activeBlobUrl = data.blobUrl;
      this.pdfSrc = data.blobUrl;
      this.zoomLevel = 100;
      this.pdfZoom = 1.0;
      this.currentPage = 1;
      setTimeout(() => {
        this.showPdfViewer = true;
        this.cdr.detectChanges();
      }, 100);
    } else {
      this.errorMessage = 'Preview is only supported for PDF files';
    }
    this.cdr.detectChanges();
  }

  private updatePdfZoom(): void {
    this.pdfZoom = this.zoomLevel / 100;
    this.cdr.detectChanges();
  }

  private fetchAndDownload(presignedUrl: string, fileName: string): Promise<void> {
    return new Promise((resolve, reject) => {
      const xhr = new XMLHttpRequest();
      xhr.responseType = 'blob';
      xhr.addEventListener('load', () => {
        if (xhr.status >= 200 && xhr.status < 300) {
          const objectUrl = URL.createObjectURL(xhr.response as Blob);
          this.triggerDownload(objectUrl, fileName);
          URL.revokeObjectURL(objectUrl);
          resolve();
          return;
        }
        reject(new Error(`Download failed with status ${xhr.status}`));
      });
      xhr.addEventListener('error', () => reject(new Error('Network error during download')));
      xhr.open('GET', presignedUrl, true);
      xhr.send();
    });
  }

  private triggerDownload(objectUrl: string, fileName: string): void {
    const anchor = document.createElement('a');
    anchor.href = objectUrl;
    anchor.download = fileName;
    anchor.style.display = 'none';
    document.body.appendChild(anchor);
    anchor.click();
    document.body.removeChild(anchor);
  }

  private cleanupBlobUrl(): void {
    if (this.activeBlobUrl) {
      this.filePreviewService.revokeBlobUrl(this.activeBlobUrl);
      this.activeBlobUrl = null;
    }
    this.pdfSrc = null;
    this.showPdfViewer = false;
  }
}
